// src/hooks/useCMSContent.js
import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to load CMS content with loading and error states
 * @param {Function} loaderFn - Async function that loads content
 * @param {*} fallbackData - Fallback data if loading fails
 * @returns {Object} - { data, loading, error }
 */
export const useCMSContent = (loaderFn, fallbackData = null) => {
  const [data, setData] = useState(fallbackData);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only load once per component mount
    if (hasLoadedRef.current) return;
    
    let mounted = true;
    hasLoadedRef.current = true;

    const loadContent = async () => {
      try {
        const result = await loaderFn();
        
        if (mounted && result) {
          // Only update if we got valid data
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          console.warn('CMS content not available, using fallback:', err.message);
          setError(err);
          // Keep fallback data
        }
      }
    };

    loadContent();

    return () => {
      mounted = false;
    };
    // Empty deps - only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, error };
};

/**
 * Hook to load multiple CMS content sources
 * @param {Array<Function>} loaderFns - Array of loader functions
 * @param {Array} fallbackData - Array of fallback data
 * @returns {Object} - { data: Array, loading, error }
 */
export const useCMSContentMultiple = (loaderFns, fallbackData = []) => {
  const [data, setData] = useState(fallbackData);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only load once per component mount
    if (hasLoadedRef.current) return;
    
    let mounted = true;
    hasLoadedRef.current = true;

    const loadAllContent = async () => {
      try {
        const results = await Promise.all(
          loaderFns.map((fn, idx) => 
            fn().catch(() => {
              console.warn('CMS content not available, using fallback');
              return fallbackData[idx] || null;
            })
          )
        );
        
        if (mounted) {
          // Only update if we got some valid data
          const validResults = results.filter(r => r !== null);
          if (validResults.length > 0) {
            setData(results);
          }
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          console.warn('Error loading CMS content:', err.message);
          setError(err);
          // Keep fallback data
        }
      }
    };

    loadAllContent();

    return () => {
      mounted = false;
    };
    // Empty deps - only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, error };
};

export default useCMSContent;
